/**
 * LienWaiverPanel — generate conditional / unconditional lien waiver PDFs.
 * After generate: Saved badge + Edit + toast (LienTab #337 pattern).
 */

import { useRef, useState, FormEvent } from 'react'
import {
  createLienDoc,
  type CreateLienDocInput,
  type LienDocType,
} from '../../../api/lienDocs'
import { parseMoneyInput } from '../../../lib/formatters'
import MoneyInput from '../../../components/MoneyInput'
import SavedBadge from '../../../components/SavedBadge'
import { useActionToast } from '../../../lib/useActionToast'

const lbl = { display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--ink3)', marginBottom: 4 } as const
const fld = { width: '100%', padding: '8px 10px', fontSize: 13 } as const

const DOC_TYPES: { value: LienDocType; label: string }[] = [
  { value: 'conditional_waiver', label: 'Conditional Waiver – Progress Payment' },
  { value: 'unconditional_waiver', label: 'Unconditional Waiver – Progress Payment' },
  { value: 'conditional_final_waiver', label: 'Conditional Waiver – Final Payment' },
  { value: 'unconditional_final_waiver', label: 'Unconditional Waiver – Final Payment' },
]

interface Props {
  projectId: number
  payAppId?: number
  onGenerated: () => void
}

function typeLabel(value: LienDocType): string {
  return DOC_TYPES.find((t) => t.value === value)?.label ?? value
}

export default function LienWaiverPanel({ projectId, payAppId, onGenerated }: Props) {
  const { showToast, Toast } = useActionToast()
  const saveInFlight = useRef(false)
  const [docType, setDocType] = useState<LienDocType>('conditional_waiver')
  const [signatoryName, setSignatoryName] = useState('')
  const [signatoryTitle, setSignatoryTitle] = useState('')
  const [throughDate, setThroughDate] = useState('')
  const [amount, setAmount] = useState('')
  const [makerOfCheck, setMakerOfCheck] = useState('')
  const [payableTo, setPayableTo] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState(true)
  const [hasConfirmedSave, setHasConfirmedSave] = useState(false)
  const [savedType, setSavedType] = useState<LienDocType>('conditional_waiver')
  const [savedName, setSavedName] = useState('')

  const canGenerate = signatoryName.trim().length > 0
  const showSavedView = hasConfirmedSave && !editing

  function resetForm() {
    setSignatoryName('')
    setSignatoryTitle('')
    setThroughDate('')
    setAmount('')
    setMakerOfCheck('')
    setPayableTo('')
    setDocType('conditional_waiver')
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (saveInFlight.current || busy) return
    if (!signatoryName.trim()) {
      setError('Signatory name is required')
      return
    }
    saveInFlight.current = true
    setBusy(true)
    setError(null)
    const input: CreateLienDocInput = {
      doc_type: docType,
      signatory_name: signatoryName.trim(),
      jurisdiction: 'california',
      ...(signatoryTitle.trim() ? { signatory_title: signatoryTitle.trim() } : {}),
      ...(throughDate ? { through_date: throughDate } : {}),
      ...(amount ? { amount: parseMoneyInput(amount) } : {}),
      ...(makerOfCheck.trim() ? { maker_of_check: makerOfCheck.trim() } : {}),
      ...(payableTo.trim() ? { check_payable_to: payableTo.trim() } : {}),
      ...(payAppId != null ? { pay_app_id: payAppId } : {}),
    }
    try {
      await createLienDoc(projectId, input)
      setSavedType(docType)
      setSavedName(signatoryName.trim())
      setHasConfirmedSave(true)
      setEditing(false)
      showToast('Lien waiver generated')
      resetForm()
      onGenerated()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate waiver')
    } finally {
      setBusy(false)
      saveInFlight.current = false
    }
  }

  if (showSavedView) {
    return (
      <div className="panel" style={{ padding: 14, marginBottom: 12 }} data-testid="lien-waiver-saved">
        {Toast}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)' }}>
            {typeLabel(savedType)}{savedName ? ` · ${savedName}` : ''}
          </span>
          <SavedBadge testId="lien-waiver-saved-badge" />
          <button
            type="button"
            className="btn b-ol btn-sm"
            data-testid="lien-waiver-edit"
            onClick={() => setEditing(true)}
          >
            Edit
          </button>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="panel" style={{ padding: 14, marginBottom: 12 }}
      data-testid="lien-waiver-form">
      {Toast}
      <label style={{ display: 'block', marginBottom: 10 }}>
        <span style={lbl}>Document type</span>
        <select className="fld-i" style={fld} value={docType}
          onChange={e => setDocType(e.target.value as LienDocType)} required>
          {DOC_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <label><span style={lbl}>Signatory name *</span>
          <input className="fld-i" style={fld} required value={signatoryName}
            onChange={e => setSignatoryName(e.target.value)} /></label>
        <label><span style={lbl}>Signatory title</span>
          <input className="fld-i" style={fld} value={signatoryTitle}
            onChange={e => setSignatoryTitle(e.target.value)} /></label>
        <label><span style={lbl}>Through date</span>
          <input type="date" className="fld-i" style={fld} value={throughDate}
            onChange={e => setThroughDate(e.target.value)} /></label>
        <label><span style={lbl}>Amount ($)</span>
          <MoneyInput className="fld-i" style={fld} value={amount}
            onChange={setAmount} /></label>
        <label><span style={lbl}>Maker of check</span>
          <input className="fld-i" style={fld} value={makerOfCheck}
            onChange={e => setMakerOfCheck(e.target.value)} /></label>
        <label><span style={lbl}>Payable to</span>
          <input className="fld-i" style={fld} value={payableTo}
            onChange={e => setPayableTo(e.target.value)} /></label>
      </div>
      {error && <p style={{ fontSize: 12, color: 'var(--red)', margin: '0 0 10px' }}>{error}</p>}
      <button
        type="submit"
        className="btn b-dk btn-sm"
        data-testid="lien-waiver-generate"
        disabled={busy || !canGenerate}
      >
        {busy ? 'Generating…' : 'Generate waiver'}
      </button>
    </form>
  )
}
